import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { astrologyEndPoint } from "../Services/AllApi";
import toast from "react-hot-toast";
import { apiConnector } from "../Services/ApiConnector";
import { motion, AnimatePresence } from "framer-motion";
// import astrologyImage from "../Assets/astrologyFront.jpg"; // Unused in new UI

// New Astrology components
import AstrologyHeader from "../component/core/AstrologyPage/AstrologyHeader";
import AdvancedHoroscopeFeature from "../component/core/AstrologyPage/AdvancedHoroscopeFeature";
import KundliFeature from "../component/core/AstrologyPage/KundliFeature";
import MatchMakingFeature from "../component/core/AstrologyPage/MatchMakingFeature";
import TestimonialsSection from "../component/core/AstrologyPage/TestimonialsSection";
import Footer from "../component/common/Footer";
import usePageTitle from "../hooks/usePageTitle";
import { useLanguage } from "../i18n/LanguageContext";

const Astrology = () => {
  usePageTitle('Astrology & Horoscope');
  const { t } = useLanguage();
  
  // State for active section toggle
  const [activeSection, setActiveSection] = useState('horoscope'); // 'horoscope', 'kundli', or 'matching'
  
  const {
    register,
    setValue,
    handleSubmit,
    // formState: { errors }, // Unused in new UI
  } = useForm();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  const { CREATE_FORM_API } = astrologyEndPoint;
  const onSubmit = async (data) => {
    const { fullName, address, phoneNum, dob, timeOfBirth, placeOfBirth } =
      data;
    const toastid = toast.loading("Please wait..");
    try {
      await apiConnector("POST", CREATE_FORM_API, {
        fullName,
        address,
        phoneNum,
        dob,
        timeOfBirth,
        placeOfBirth,
      });
      toast.success("Thank you!");
      setValue("fullName", " ");
      setValue("address", " ");
      setValue("phoneNum", " ");
      setValue("dob", " ");
      setValue("timeOfBirth", " ");
      setValue("placeOfBirth", " ");
    } catch (error) {
      console.log(error);
    }
    toast.dismiss(toastid);
  };
  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Astrology Header */}
      <motion.div variants={sectionVariants}>
        <AstrologyHeader />
      </motion.div>

      {/* Toggle Section */}
      <motion.section 
        className="py-4 bg-gradient-to-r from-orange-100 to-amber-100"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center mb-4">
              <div className="bg-white rounded-2xl p-1.5 sm:p-2 shadow-xl border border-orange-200 backdrop-blur-sm">
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-0">
                  <motion.button
                    onClick={() => setActiveSection('horoscope')}
                    className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                      activeSection === 'horoscope'
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                        : 'text-orange-600 hover:bg-orange-50 hover:text-orange-700'
                    }`}
                    whileHover={{ scale: activeSection === 'horoscope' ? 1.02 : 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <span>🌟</span>
                      <span>{t('dailyHoroscope')}</span>
                    </span>
                  </motion.button>
                  <motion.button
                    onClick={() => setActiveSection('kundli')}
                    className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap sm:ml-1 ${
                      activeSection === 'kundli'
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                        : 'text-orange-600 hover:bg-orange-50 hover:text-orange-700'
                    }`}
                    whileHover={{ scale: activeSection === 'kundli' ? 1.02 : 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <span>📜</span>
                      <span>{t('kundliGeneration')}</span>
                    </span>
                  </motion.button>
                  <motion.button
                    onClick={() => setActiveSection('matching')}
                    className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap sm:ml-1 ${
                      activeSection === 'matching'
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                        : 'text-orange-600 hover:bg-orange-50 hover:text-orange-700'
                    }`}
                    whileHover={{ scale: activeSection === 'matching' ? 1.02 : 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <span>💕</span>
                      <span>{t('kundliMatching')}</span>
                    </span>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* API Features Section */}
      <motion.section 
        className="py-6 bg-white"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Content Sections with AnimatePresence for smooth transitions */}
            <AnimatePresence mode="wait">
              {/* Advanced Horoscope Feature - Show only when horoscope is active */}
              {activeSection === 'horoscope' && (
                <motion.div
                  key="horoscope-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.4, 
                    ease: "easeOut"
                  }}
                >
                  {/* Section Title */}
                  <motion.div 
                    className="text-center mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                  </motion.div>
                  <AdvancedHoroscopeFeature />
                </motion.div>
              )}
              
              {/* Kundli Feature - Show only when kundli is active */}
              {activeSection === 'kundli' && (
                <motion.div
                  key="kundli-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.4, 
                    ease: "easeOut"
                  }}
                >
                  {/* Section Title */}
                  <motion.div 
                    className="text-center mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    
                  </motion.div>
                  <KundliFeature />
                </motion.div>
              )}

              {/* Kundli Matching Feature - Show only when matching is active */}
              {activeSection === 'matching' && (
                <motion.div
                  key="matching-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.4, 
                    ease: "easeOut"
                  }}
                >
                  {/* Section Title */}
                  <motion.div 
                    className="text-center mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                  
                  </motion.div>
                  <MatchMakingFeature />
                </motion.div>
              )}
            </AnimatePresence>


          </div>
        </div>
      </motion.section>

      {/* Testimonials */}
      <motion.div variants={sectionVariants}>
        <TestimonialsSection />
      </motion.div>

      {/* Footer */}
      <motion.div variants={sectionVariants}>
        <Footer />
      </motion.div>

      {/* Hidden Form for API Integration - Keep existing functionality */}
      <div className="hidden">
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="text"
            placeholder="Enter full name here"
            {...register("fullName", { required: true })}
          />
          <input
            type="number"
            placeholder="Whatsapp Number"
            {...register("phoneNum", {
              required: {
                value: true,
                message: "Please enter Phone Number",
              },
              maxLength: { value: 10, message: "Invalid Phone Number" },
              minLength: { value: 8, message: "Invalid Phone Number" },
            })}
          />
          <input
            type="text"
            placeholder="Enter address here"
            {...register("address", { required: false })}
          />
          <input
            type="date"
            {...register("dob", { required: false })}
          />
          <input
            type="text"
            {...register("placeOfBirth", { required: false })}
            placeholder="Enter place of birth here"
          />
          <input
            type="time"
            {...register("timeOfBirth", { required: false })}
          />
          <button type="submit">Submit</button>
        </form>
      </div>
    </motion.div>
  );
};

export default Astrology;
